import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '../services/api';
import type { Team, TeamMember, TeamFolder, TeamChatMember, TeamChatSession, TeamChatMemberInfo } from '../services/api/teams-api';

// Re-export types for convenience
export type { Team, TeamMember, TeamFolder, TeamChatMember, TeamChatSession, TeamChatMemberInfo };

// ===== TYPES =====

interface TeamState {
  // Data
  teams: Team[];
  activeTeam: Team | null;
  activeImageFolder: TeamFolder | null;
  activeVideoFolder: TeamFolder | null;
  members: TeamMember[];
  imageFolders: TeamFolder[];
  videoFolders: TeamFolder[];
  
  // Team Chat Mode data
  chatMembers: TeamChatMember[];
  selectedChatMember: TeamChatMember | null;
  selectedChatMemberInfo: TeamChatMemberInfo | null;
  chatMemberSessions: TeamChatSession[];
  viewingOtherUserChat: boolean; // True when viewing someone else's chat (read-only)
  currentUserId: string | null; // Current user's telegram ID for comparison
  
  // Mode
  isTeamMode: boolean;
  memberFilter: 'all' | 'mine' | string; // 'all', 'mine', or specific userId
  
  // Loading states
  isLoading: boolean;
  isLoadingMembers: boolean;
  isLoadingFolders: boolean;
  isLoadingChatMembers: boolean;
  isLoadingChatSessions: boolean;
  error: string | null;
  
  // Actions
  fetchTeams: () => Promise<void>;
  setActiveTeam: (teamId: string | null) => void;
  setActiveImageFolder: (folder: TeamFolder | null) => void;
  setActiveVideoFolder: (folder: TeamFolder | null) => void;
  toggleTeamMode: (enabled: boolean) => void;
  setMemberFilter: (filter: 'all' | 'mine' | string) => void;
  
  // Team CRUD
  createTeam: (name: string, avatarUrl?: string) => Promise<Team>;
  updateTeam: (teamId: string, data: { name?: string; avatarUrl?: string }) => Promise<void>;
  deleteTeam: (teamId: string) => Promise<void>;
  
  // Members
  fetchMembers: (teamId: string) => Promise<void>;
  removeMember: (teamId: string, userId: string) => Promise<void>;
  updateMemberRole: (teamId: string, userId: string, role: 'admin' | 'member') => Promise<void>;
  leaveTeam: (teamId: string) => Promise<void>;
  
  // Folders
  fetchFolders: (teamId: string, folderType?: 'image' | 'video' | 'mixed') => Promise<void>;
  createFolder: (teamId: string, name: string, folderType?: 'image' | 'video' | 'mixed') => Promise<TeamFolder>;
  renameFolder: (teamId: string, folderId: string, newName: string) => Promise<void>;
  deleteFolder: (teamId: string, folderId: string) => Promise<void>;
  setFolderVisibility: (teamId: string, folderId: string, visibility: 'private' | 'members' | 'shared', memberIds?: string[]) => Promise<void>;
  
  // Tokens
  updateTeamTokens: (teamId: string, newBalance: number) => void;
  
  // Team Chat Mode actions
  fetchChatMembers: (teamId: string) => Promise<void>;
  selectChatMember: (member: TeamChatMember | null) => void;
  fetchChatMemberSessions: (teamId: string, userId: string) => Promise<void>;
  clearChatMemberSelection: () => void;
  setViewingOtherUserChat: (isViewing: boolean) => void;
  updateChatSessionName: (sessionId: string, newName: string) => void;
  
  // Reset
  reset: () => void;
}

// ===== STORE =====

export const useTeamStore = create<TeamState>()(
  persist(
    (set, get) => ({
      // Initial state
      teams: [],
      activeTeam: null,
      activeImageFolder: null,
      activeVideoFolder: null,
      members: [],
      imageFolders: [],
      videoFolders: [],
      
      // Team Chat Mode state
      chatMembers: [],
      selectedChatMember: null,
      selectedChatMemberInfo: null,
      chatMemberSessions: [],
      viewingOtherUserChat: false,
      currentUserId: null,
      
      isTeamMode: false,
      memberFilter: 'all',
      isLoading: false,
      isLoadingMembers: false,
      isLoadingFolders: false,
      isLoadingChatMembers: false,
      isLoadingChatSessions: false,
      error: null,

      // ===== FETCH TEAMS =====
      fetchTeams: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiClient.teams.getTeams();
          if (response.success) {
            set({ teams: response.teams, isLoading: false });
            
            // If activeTeam was set (from persist), update it with fresh data
            const { activeTeam, isTeamMode } = get();
            if (activeTeam || isTeamMode) {
              const teamId = activeTeam?.id;
              if (teamId) {
                const updatedTeam = response.teams.find(t => t.id === teamId);
                if (updatedTeam) {
                  // Restore full team data and ensure isTeamMode is true
                  set({ activeTeam: updatedTeam, isTeamMode: true });
                  // Fetch members for restored team
                  // NOTE: Folders are fetched by Gallery page when needed
                  get().fetchMembers(teamId);
                } else {
                  // Team was deleted or user is no longer a member
                  set({ activeTeam: null, isTeamMode: false, activeImageFolder: null, activeVideoFolder: null });
                }
              } else if (isTeamMode) {
                // isTeamMode is true but no activeTeam - reset
                set({ isTeamMode: false });
              }
            }
          }
        } catch (error: any) {
          set({ 
            isLoading: false, 
            error: error.response?.data?.message || 'Failed to load teams' 
          });
        }
      },

      // ===== SET ACTIVE TEAM =====
      setActiveTeam: (teamId: string | null) => {
        if (!teamId) {
          set({
            activeTeam: null,
            activeImageFolder: null,
            activeVideoFolder: null,
            isTeamMode: false,
            memberFilter: 'all',
            // Clear chat member selection when switching to personal mode
            selectedChatMember: null,
            selectedChatMemberInfo: null,
            chatMemberSessions: [],
            viewingOtherUserChat: false,
            chatMembers: []
          });
          return;
        }
        
        const { teams, activeTeam } = get();
        const cachedTeam = teams.find(t => t.id === teamId);
        const isSwitchingTeam = activeTeam && activeTeam.id !== teamId;
        
        // Если команда найдена в кэше - используем её сразу
        if (cachedTeam) {
          set({
            activeTeam: cachedTeam,
            activeImageFolder: null,
            activeVideoFolder: null,
            isTeamMode: true,
            memberFilter: 'mine',
            // Clear chat member selection when switching teams
            ...(isSwitchingTeam && {
              selectedChatMember: null,
              selectedChatMemberInfo: null,
              chatMemberSessions: [],
              viewingOtherUserChat: false,
              chatMembers: []
            })
          });
          // Members will be fetched after fresh team data loads below
        } else {
          // Команда не найдена в кэше - устанавливаем режим команды
          // и ждём загрузки данных с сервера
          set({
            activeImageFolder: null,
            activeVideoFolder: null,
            isTeamMode: true,
            memberFilter: 'mine',
            ...(isSwitchingTeam && {
              selectedChatMember: null,
              selectedChatMemberInfo: null,
              chatMemberSessions: [],
              viewingOtherUserChat: false,
              chatMembers: []
            })
          });
        }
        
        // Всегда загружаем свежие данные команды с сервера
        // Это также установит activeTeam если команда не была в кэше
        apiClient.teams.getTeam(teamId).then(response => {
          if (response.success && response.team) {
            const freshTeam = response.team;
            set((state) => {
              // Обновляем или добавляем команду в кэш
              const teamExists = state.teams.some(t => t.id === teamId);
              return {
                teams: teamExists 
                  ? state.teams.map(t => t.id === teamId ? freshTeam : t)
                  : [...state.teams, freshTeam],
                // Устанавливаем activeTeam если мы всё ещё в режиме этой команды
                activeTeam: state.isTeamMode ? freshTeam : state.activeTeam
              };
            });
            // Fetch members after team is loaded
            get().fetchMembers(teamId);
          }
        }).catch(err => {
          console.warn('Failed to fetch fresh team data:', err);
        });
      },

      // ===== SET ACTIVE FOLDER =====
      setActiveImageFolder: (folder: TeamFolder | null) => {
        set({ activeImageFolder: folder });
      },

      setActiveVideoFolder: (folder: TeamFolder | null) => {
        set({ activeVideoFolder: folder });
      },

      // ===== TOGGLE TEAM MODE =====
      toggleTeamMode: (enabled: boolean) => {
        if (!enabled) {
          set({
            isTeamMode: false,
            memberFilter: 'all',
            activeTeam: null,
            activeImageFolder: null,
            activeVideoFolder: null,
            // Reset chat viewing state
            selectedChatMember: null,
            selectedChatMemberInfo: null,
            chatMemberSessions: [],
            viewingOtherUserChat: false,
            chatMembers: []
          });
        } else {
          // ВАЖНО: Включаем team mode только если есть activeTeam
          // Иначе будет рассинхронизация состояния
          const { activeTeam } = get();
          if (activeTeam?.id) {
            set({ isTeamMode: true, memberFilter: 'mine' });
          } else {
            console.warn('⚠️ toggleTeamMode(true) called but no activeTeam is set');
            // Не включаем team mode без activeTeam
          }
        }
      },

      // ===== SET MEMBER FILTER =====
      setMemberFilter: (filter: 'all' | 'mine' | string) => {
        set({ memberFilter: filter });
      },

      // ===== CREATE TEAM =====
      createTeam: async (name: string, avatarUrl?: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await apiClient.teams.createTeam(name, undefined, avatarUrl);
          if (response.success && response.team) {
            const newTeam = response.team;
            set((state) => ({
              teams: [newTeam, ...state.teams],
              isLoading: false
            }));
            return newTeam;
          }
          throw new Error('Failed to create team');
        } catch (error: any) {
          set({ 
            isLoading: false, 
            error: error.response?.data?.message || 'Failed to create team' 
          });
          throw error;
        }
      },

      // ===== UPDATE TEAM =====
      updateTeam: async (teamId: string, data: { name?: string; avatarUrl?: string }) => {
        try {
          const response = await apiClient.teams.updateTeam(teamId, data);
          if (response.success && response.team) {
            set((state) => ({
              teams: state.teams.map(t => t.id === teamId ? { ...t, ...response.team } : t),
              activeTeam: state.activeTeam?.id === teamId ? { ...state.activeTeam, ...response.team } : state.activeTeam
            }));
          }
        } catch (error: any) {
          set({ error: error.response?.data?.message || 'Failed to update team' });
          throw error;
        }
      },

      // ===== DELETE TEAM =====
      deleteTeam: async (teamId: string) => {
        try {
          const response = await apiClient.teams.deleteTeam(teamId);
          if (response.success) {
            set((state) => ({
              teams: state.teams.filter(t => t.id !== teamId),
              activeTeam: state.activeTeam?.id === teamId ? null : state.activeTeam,
              isTeamMode: state.activeTeam?.id === teamId ? false : state.isTeamMode
            }));
          }
        } catch (error: any) {
          set({ error: error.response?.data?.message || 'Failed to delete team' });
          throw error;
        }
      },

      // ===== FETCH MEMBERS =====
      fetchMembers: async (teamId: string) => {
        set({ isLoadingMembers: true });
        try {
          const response = await apiClient.teams.getTeamMembers(teamId);
          if (response.success) {
            set({ members: response.members, isLoadingMembers: false });
          }
        } catch (error: any) {
          set({ isLoadingMembers: false });
          console.error('Failed to fetch members:', error);
        }
      },

      // ===== REMOVE MEMBER =====
      removeMember: async (teamId: string, userId: string) => {
        try {
          const response = await apiClient.teams.removeTeamMember(teamId, userId);
          if (response.success) {
            set((state) => ({
              members: state.members.filter(m => m.userId !== userId)
            }));
            // Refresh team to update member count
            get().fetchTeams();
          }
        } catch (error: any) {
          throw error;
        }
      },

      // ===== UPDATE MEMBER ROLE =====
      updateMemberRole: async (teamId: string, userId: string, role: 'admin' | 'member') => {
        try {
          const response = await apiClient.teams.updateMemberRole(teamId, userId, role);
          if (response.success) {
            set((state) => ({
              members: state.members.map(m => 
                m.userId === userId ? { ...m, role } : m
              )
            }));
          }
        } catch (error: any) {
          throw error;
        }
      },

      // ===== LEAVE TEAM =====
      leaveTeam: async (teamId: string) => {
        try {
          const response = await apiClient.teams.leaveTeam(teamId);
          if (response.success) {
            set((state) => ({
              teams: state.teams.filter(t => t.id !== teamId),
              activeTeam: state.activeTeam?.id === teamId ? null : state.activeTeam,
              isTeamMode: state.activeTeam?.id === teamId ? false : state.isTeamMode
            }));
          }
        } catch (error: any) {
          throw error;
        }
      },

      // ===== FETCH FOLDERS =====
      fetchFolders: async (teamId: string, folderType?: 'image' | 'video' | 'mixed') => {
        set({ isLoadingFolders: true });
        try {
          if (folderType === 'image' || folderType === 'video') {
            // Fetch only the specified type
            const response = await apiClient.teams.getTeamFolders(teamId, folderType);
            if (response.success) {
              if (folderType === 'image') {
                set({ imageFolders: response.folders, isLoadingFolders: false });
              } else {
                set({ videoFolders: response.folders, isLoadingFolders: false });
              }
            }
          } else {
            // For 'mixed' or undefined, fetch BOTH types separately to ensure correct categorization
            const [imageResponse, videoResponse] = await Promise.all([
              apiClient.teams.getTeamFolders(teamId, 'image'),
              apiClient.teams.getTeamFolders(teamId, 'video')
            ]);
            
            set({ 
              imageFolders: imageResponse.success ? imageResponse.folders : [],
              videoFolders: videoResponse.success ? videoResponse.folders : [],
              isLoadingFolders: false 
            });
          }
        } catch (error: any) {
          set({ isLoadingFolders: false });
          console.error('Failed to fetch folders:', error);
        }
      },

      // ===== CREATE FOLDER =====
      createFolder: async (teamId: string, name: string, folderType: 'image' | 'video' | 'mixed' = 'image') => {
        try {
          const response = await apiClient.teams.createTeamFolder(teamId, name, folderType);
          if (response.success && response.folder) {
            // Add to the appropriate array based on type
            if (folderType === 'image') {
              set((state) => ({
                imageFolders: [response.folder, ...state.imageFolders]
              }));
            } else if (folderType === 'video') {
              set((state) => ({
                videoFolders: [response.folder, ...state.videoFolders]
              }));
            } else {
              // For 'mixed', add to both
              set((state) => ({
                imageFolders: [response.folder, ...state.imageFolders],
                videoFolders: [response.folder, ...state.videoFolders]
              }));
            }
            return response.folder;
          }
          throw new Error('Failed to create folder');
        } catch (error: any) {
          throw error;
        }
      },

      // ===== RENAME/DELETE FOLDER =====
      renameFolder: async (teamId: string, folderId: string, newName: string) => {
        try {
          const response = await apiClient.teams.updateTeamFolder(teamId, folderId, { name: newName });
          if (response.success) {
            set((state) => ({
              imageFolders: state.imageFolders.map(f => f.id === folderId ? { ...f, name: newName } : f),
              videoFolders: state.videoFolders.map(f => f.id === folderId ? { ...f, name: newName } : f),
              activeImageFolder: state.activeImageFolder?.id === folderId 
                ? { ...state.activeImageFolder, name: newName } 
                : state.activeImageFolder,
              activeVideoFolder: state.activeVideoFolder?.id === folderId 
                ? { ...state.activeVideoFolder, name: newName } 
                : state.activeVideoFolder
            }));
          }
        } catch (error: any) {
          throw error;
        }
      },

      deleteFolder: async (teamId: string, folderId: string) => {
        try {
          const response = await apiClient.teams.deleteTeamFolder(teamId, folderId);
          if (response.success) {
            set((state) => ({
              imageFolders: state.imageFolders.filter(f => f.id !== folderId),
              videoFolders: state.videoFolders.filter(f => f.id !== folderId),
              activeImageFolder: state.activeImageFolder?.id === folderId ? null : state.activeImageFolder,
              activeVideoFolder: state.activeVideoFolder?.id === folderId ? null : state.activeVideoFolder
            }));
          }
        } catch (error: any) {
          throw error;
        }
      },

      setFolderVisibility: async (teamId: string, folderId: string, visibility: 'private' | 'members' | 'shared', memberIds?: string[]) => {
        try {
          const response = await apiClient.teams.setTeamFolderVisibility(teamId, folderId, visibility, memberIds);
          if (response.success) {
            const updatedFolder = response.folder;
            const updateFolder = (folders: TeamFolder[]) =>
              folders.map(f => f.id === folderId ? { ...f, visibility, accessList: updatedFolder.accessList } : f);
            set((state) => ({
              imageFolders: updateFolder(state.imageFolders),
              videoFolders: updateFolder(state.videoFolders),
            }));
          }
        } catch (error: any) {
          throw error;
        }
      },

      // ===== UPDATE TEAM TOKENS =====
      updateTeamTokens: (teamId: string, newBalance: number) => {
        set((state) => ({
          teams: state.teams.map(t => 
            t.id === teamId ? { ...t, tokens: newBalance } : t
          ),
          activeTeam: state.activeTeam?.id === teamId 
            ? { ...state.activeTeam, tokens: newBalance } 
            : state.activeTeam
        }));
      },

      // ===== TEAM CHAT MODE =====
      // Two-level structure: members -> their team chats
      
      fetchChatMembers: async (teamId: string) => {
        set({ isLoadingChatMembers: true });
        try {
          const response = await apiClient.teams.getTeamChatMembers(teamId);
          if (response.success) {
            set({ 
              chatMembers: response.members, 
              currentUserId: response.currentUserId,
              isLoadingChatMembers: false 
            });
          }
        } catch (error: any) {
          set({ isLoadingChatMembers: false });
          console.error('Failed to fetch team chat members:', error);
        }
      },

      selectChatMember: (member: TeamChatMember | null) => {
        if (!member) {
          set({ 
            selectedChatMember: null, 
            selectedChatMemberInfo: null,
            chatMemberSessions: [],
            viewingOtherUserChat: false 
          });
        } else {
          const { currentUserId } = get();
          set({ 
            selectedChatMember: member,
            viewingOtherUserChat: member.userId !== currentUserId
          });
        }
      },

      fetchChatMemberSessions: async (teamId: string, userId: string) => {
        set({ isLoadingChatSessions: true });
        try {
          const response = await apiClient.teams.getTeamMemberSessions(teamId, userId);
          if (response.success) {
            set({ 
              chatMemberSessions: response.sessions,
              selectedChatMemberInfo: response.user,
              viewingOtherUserChat: !response.isOwnChats,
              isLoadingChatSessions: false 
            });
          }
        } catch (error: any) {
          set({ isLoadingChatSessions: false });
          console.error('Failed to fetch member team chat sessions:', error);
        }
      },

      clearChatMemberSelection: () => {
        set({ 
          selectedChatMember: null,
          selectedChatMemberInfo: null,
          chatMemberSessions: [],
          viewingOtherUserChat: false
        });
      },

      setViewingOtherUserChat: (isViewing: boolean) => {
        set({ viewingOtherUserChat: isViewing });
      },

      updateChatSessionName: (sessionId: string, newName: string) => {
        set((state) => ({
          chatMemberSessions: state.chatMemberSessions.map(session =>
            session.id === sessionId ? { ...session, name: newName } : session
          )
        }));
      },

      // ===== RESET =====
      reset: () => {
        set({
          teams: [],
          activeTeam: null,
          activeImageFolder: null,
          activeVideoFolder: null,
          members: [],
          imageFolders: [],
          videoFolders: [],
          chatMembers: [],
          selectedChatMember: null,
          selectedChatMemberInfo: null,
          chatMemberSessions: [],
          viewingOtherUserChat: false,
          currentUserId: null,
          isTeamMode: false,
          memberFilter: 'all',
          isLoading: false,
          isLoadingMembers: false,
          isLoadingFolders: false,
          isLoadingChatMembers: false,
          isLoadingChatSessions: false,
          error: null
        });
      }
    }),
    {
      name: 'team-storage',
      partialize: (state) => ({
        // Only persist essential data — memberFilter is derived from isTeamMode, not persisted
        activeTeam: state.activeTeam ? { id: state.activeTeam.id } : null,
        isTeamMode: state.isTeamMode
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Validate persisted activeTeam.id is a real UUID, not "undefined" or garbage
        const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const id = state.activeTeam?.id;
        if (state.isTeamMode && (!id || !UUID_RE.test(id))) {
          // Corrupted persist state — reset team mode
          state.activeTeam = null;
          state.isTeamMode = false;
        }
      }
    }
  )
);

